import { useState } from 'react';
import { motion } from 'framer-motion';
import data from '../data/data.json';
import MagneticButton from './MagneticButton';

export default function Contact() {
  const { contact, meta } = data;
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(meta.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2400);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section
      id="contact"
      className="section"
      style={{
        background: 'linear-gradient(180deg, rgba(250,246,238,0.2) 0%, rgba(247,190,161,0.18) 60%, rgba(250,246,238,0.9) 100%)',
        position: 'relative',
        overflow: 'hidden',
        paddingBottom: '3rem',
      }}
    >
      {/* Soft Ambient Radial Light Spill */}
      <div
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232, 149, 109, 0.25) 0%, rgba(247, 190, 161, 0.1) 50%, transparent 75%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: '780px', margin: '0 auto', textAlign: 'center' }}>
          <motion.p
            className="section-label"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ justifyContent: 'center' }}
          >
            Initiate Conversation
          </motion.p>

          <motion.h2
            className="foil-shine"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(2.8rem, 6.5vw, 4.8rem)',
              fontWeight: 700,
              lineHeight: 1.08,
              marginBottom: '1.5rem',
              letterSpacing: '-0.025em',
            }}
          >
            {contact.heading}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              fontSize: '1.15rem',
              color: 'var(--text-muted)',
              marginBottom: '3rem',
              lineHeight: 1.75,
            }}
          >
            {contact.subtext}
          </motion.p>

          {/* Interactive Copy Email Pill Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.75rem 1.6rem',
              borderRadius: '100px',
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1.5px solid var(--border-accent)',
              boxShadow: '0 8px 28px rgba(232, 149, 109, 0.2)',
              marginBottom: '2.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>
              {meta.email}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleCopyEmail}
                data-cursor-hover
                style={{
                  padding: '0.4rem 0.9rem',
                  borderRadius: '100px',
                  background: copied ? '#3a7050' : 'var(--text)',
                  color: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  transition: 'background-color 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer',
                }}
              >
                {copied ? '✓ Copied!' : '📋 Copy'}
              </button>
              <a
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=${meta.email}`}
                target="_blank"
                rel="noreferrer"
                data-cursor-hover
                style={{
                  padding: '0.4rem 0.9rem',
                  borderRadius: '100px',
                  background: 'rgba(45, 23, 83, 0.08)',
                  color: 'var(--text)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                Send Email ↗
              </a>
            </div>
          </motion.div>

          {/* Quick Social & Portfolio Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '4rem' }}
          >
            <MagneticButton strength={0.3}>
              <a href={meta.linkedin} className="btn btn-outline" target="_blank" rel="noreferrer" data-cursor-hover>
                LinkedIn ↗
              </a>
            </MagneticButton>
            <MagneticButton strength={0.3}>
              <a href={meta.github} className="btn btn-outline" target="_blank" rel="noreferrer" data-cursor-hover>
                GitHub ↗
              </a>
            </MagneticButton>
            <MagneticButton strength={0.3}>
              <a href={meta.resume} className="btn btn-outline" target="_blank" rel="noreferrer" data-cursor-hover>
                Resume PDF ↗
              </a>
            </MagneticButton>
          </motion.div>
        </div>

        {/* Footer Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          style={{
            marginTop: '3rem',
            paddingTop: '2.5rem',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1.25rem',
          }}
        >
          <div>
            <p style={{ fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>
              {meta.name} — Portfolio
            </p>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Integrated M.Tech CS · VIT University · Vellore, India
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-faint)' }}>
              React • Framer Motion • Three.js
            </span>
            <button
              onClick={scrollToTop}
              data-cursor-hover
              style={{
                fontSize: '0.82rem',
                fontWeight: 600,
                color: 'var(--accent-dark)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                cursor: 'pointer',
              }}
            >
              Back to top ↑
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
